import { ValidationError } from '../../shared/errors/BaseErrors';
import { ChannelType } from '../../shared/types/Channel';
import { ChannelLink } from '../value-objects/ChannelLink';
import { ChannelAdministrationRules } from '../services/ChannelAdministrationRules';

export type ChannelImportDraftStatus = 'resolving' | 'ready' | 'imported' | 'failed';

export const MAX_CHANNEL_IMPORT_LINKS = 500;

export interface ChannelImportRow {
  link: string;
  title: string;
  type: ChannelType.FREE | ChannelType.PAID;
  typeResolved: boolean;
  categoryName: string;
  membersCount: number;
  minimalSendingInterval: number;
  isPrivate: boolean;
  exists: boolean;
  telegramChannelId?: number;
}

export interface ChannelImportFailure {
  link: string;
  reason: string;
}

export interface ChannelImportReport {
  created: number;
  updated: number;
  failed: number;
  failures: ChannelImportFailure[];
}

export interface ChannelImportDraftProps {
  uuid: string;
  status: ChannelImportDraftStatus;
  telegramAccountUuid: string;
  totalLinks: number;
  processedLinks: number;
  skippedDuplicates: number;
  rows: ChannelImportRow[];
  failures: ChannelImportFailure[];
  errorMessage?: string;
  report?: ChannelImportReport;
  createdAt: string;
}

export const extractChannelImportLinks = (raw: string): string[] => {
  const unique = new Set<string>();
  for (const line of (raw ?? '').split('\n')) {
    if (line.trim().startsWith('#')) continue;
    for (const token of line.split(/[,;\s]+/)) {
      if (!token) continue;
      try {
        unique.add(ChannelLink.create(token).Value);
      } catch {
        continue;
      }
    }
  }
  return Array.from(unique);
};

export const parseChannelImportLinks = (raw: string): string[] => {
  const links = extractChannelImportLinks(raw);
  if (links.length === 0) throw new ValidationError('channel-import-no-links');
  if (links.length > MAX_CHANNEL_IMPORT_LINKS) throw new ValidationError('channel-import-too-many-links');
  return links;
};

export const validateChannelImportRows = (rows: ChannelImportRow[]): ChannelImportRow[] => {
  const seen = new Set<string>();
  return rows.map((row) => {
    const link = ChannelLink.create(row.link).Value;
    if (seen.has(link)) throw new ValidationError('channel-import-duplicate-link');
    seen.add(link);
    if (row.type !== ChannelType.FREE && row.type !== ChannelType.PAID) throw new ValidationError('channel-type-unknown');
    return {
      ...row,
      link,
      categoryName: (row.categoryName ?? '').trim(),
      membersCount: ChannelAdministrationRules.ensureMembersCount(row.membersCount),
      minimalSendingInterval: ChannelAdministrationRules.ensureSendingInterval(row.minimalSendingInterval),
    };
  });
};

export class ChannelImportDraftEntity {
  private readonly props: ChannelImportDraftProps;
  private constructor(props: ChannelImportDraftProps) { this.props = { ...props }; }
  static create(props: ChannelImportDraftProps) { return new ChannelImportDraftEntity(props); }
  get uuid() { return this.props.uuid; }
  get status() { return this.props.status; }
  get telegramAccountUuid() { return this.props.telegramAccountUuid; }
  get totalLinks() { return this.props.totalLinks; }
  get processedLinks() { return this.props.processedLinks; }
  get skippedDuplicates() { return this.props.skippedDuplicates; }
  get rows() { return this.props.rows; }
  get failures() { return this.props.failures; }
  get errorMessage() { return this.props.errorMessage; }
  get report() { return this.props.report; }
  get createdAt() { return this.props.createdAt; }
  get isResolving() { return this.props.status === 'resolving'; }
  get isEditable() { return this.props.status === 'ready'; }
  get progressPercent() {
    if (this.props.totalLinks === 0) return 0;
    return Math.min(100, Math.round((this.props.processedLinks / this.props.totalLinks) * 100));
  }
  get rowsNeedingReview() { return this.props.rows.filter((row) => !row.typeResolved).length; }
  get existingRows() { return this.props.rows.filter((row) => row.exists).length; }
}
